import { Button, StarFilledIcon, StarIcon } from '@calizahq/react-components-v2'
import { Form, Formik } from 'formik'
import React from 'react'

import { InputMultiSelect } from '@/components/Input/InputMultiSelect'
import { HomeQualityCheckMutations } from '@/graphql/mutations/homeQualityChecks.mutations'
import { useMobile } from '@/hooks'
import { useGraphqlMutation } from '@/hooks/useGraphql'

import { formSchema, FormValuesType, HostRatingReasons, initialValues } from './CreateHostRating.util'

type Props = {
  homeQualityCheckId: number
  refetchOnSuccess?: () => void
  toggle: () => void
}

export const CreateHostRatingForm = ({ toggle, homeQualityCheckId, refetchOnSuccess }: Props) => {
  const { isTablet } = useMobile()

  const {
    mutate,
    isError,
    isPending: isMutationLoading,
  } = useGraphqlMutation(HomeQualityCheckMutations.createHostRating(), {
    onSuccess: () => {
      refetchOnSuccess && refetchOnSuccess()
      toggle()
    },
  })

  const hostRatingReasonOptions = Object.keys(HostRatingReasons).map((key) => {
    return { label: HostRatingReasons[key], value: HostRatingReasons[key] }
  })

  const handleSubmit = (values: FormValuesType) => {
    const { comments, rating } = values
    const mutatedComments = comments.map((comment) => comment.value)

    mutate({
      homeQualityCheckId,
      comments: mutatedComments,
      rating,
    })
  }

  return (
    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={formSchema}
    >
      {({ values, setFieldValue }) => {
        const filledStars = [...Array(values.rating).keys()]
        const emptyStars = [...Array(5 - values.rating).keys()]

        return (
          <Form className="flex w-full flex-col flex-wrap px-6 md:max-w-120 h-120">
            <p className="leading-nonemt-4 mb-2 text-md">How would you rate the Host?</p>
            <div className="flex">
              {filledStars.map((index) => (
                <div
                  className={'mr-2 grid h-5 w-5 cursor-pointer items-center justify-center'}
                  key={index}
                  onClick={() => setFieldValue('rating', index + 1)}
                  role="presentation"
                >
                  <StarFilledIcon className="w-5" color="#FFD426" />
                </div>
              ))}
              {emptyStars.map((index) => (
                <div
                  className={'mr-2 grid h-5 w-5 cursor-pointer items-center justify-center'}
                  key={index}
                  onClick={() => setFieldValue('rating', index + 1 + filledStars.length)}
                  role="presentation"
                >
                  <StarIcon className="w-5" color="#FFD426" />
                </div>
              ))}
            </div>

            {values.rating < 5 ? (
              <div className="flex w-full flex-col md:mb-4 md:gap-4 mt-2">
                <InputMultiSelect
                  className="w-full"
                  id="comments"
                  label="Why aren't you giving the Host a perfect score?"
                  name="comments"
                  options={hostRatingReasonOptions}
                />
              </div>
            ) : null}

            {isError && <p className="mb-4 text-p-sm text-text-semantic-error">Error providing Host feedback.</p>}
            <div className="flex flex-col-reverse justify-between md:flex-row mt-2">
              <Button
                className="w-full bg-action-secondary md:flex md:w-auto"
                onClick={toggle}
                size={isTablet ? 'medium' : 'small'}
                variant="secondary"
              >
                Close
              </Button>
              <Button
                className="mb-2 w-full bg-text-semantic-success md:mb-0 md:w-auto"
                disabled={isMutationLoading}
                isLoading={isMutationLoading}
                size={isTablet ? 'medium' : 'small'}
                type="submit"
              >
                Submit
              </Button>
            </div>
          </Form>
        )
      }}
    </Formik>
  )
}
